import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import {
  Sparkle, Calculator, ArrowLeft, Sun, Heart, Drop, Flame, Wind, Eye, Crown,
  Mountains, ChartBar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import { useSavedReportLoader } from "../lib/useSavedReport";

const PURUSHARTHA_ICONS = {
  dharma: Sun,
  artha: Mountains,
  kama: Heart,
  moksha: Crown,
};

const CHAKRA_ICONS = {
  sahasrara: Crown,
  ajna: Eye,
  vishuddha: Wind,
  anahata: Heart,
  manipura: Flame,
  svadhisthana: Drop,
  muladhara: Mountains,
};

export default function ChakraAnalysisPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSavedReportLoader({ expectedTool: "chakras", setForm, setResult });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/chakras/compute", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='chakra-result']")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your chakras. Please re-check your inputs.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="chakra-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4" data-testid="chakra-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Sparkle size={14} weight="duotone" /> Free · Vedic-Tantric Energy Map
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Chakra & <span className="italic text-gold">Purushartha</span> Heat-Map
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          Your kundali isn't just planets — it's an energetic signature. We map your Lagna, Sun, Moon and all nine grahas onto the seven chakras to show which energy centre runs your life most, and onto the four <b className="text-[#3F2D1B]">Purusharthas</b> (Dharma · Artha · Kama · Moksha) to show which life-pursuit your soul came here for.
        </p>

        {/* Form */}
        <form onSubmit={analyze} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.date} onChange={handle("date")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="chakra-date" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth</span>
              <input type="time" required value={form.time} onChange={handle("time")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="chakra-time" />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Place of birth</span>
              <input type="text" required value={form.place} onChange={handle("place")} placeholder="Mumbai, India" className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="chakra-place" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Timezone</span>
              <select value={form.timezone} onChange={handle("timezone")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="chakra-tz">
                <option value="Asia/Kolkata">India (IST · UTC+5:30)</option>
                <option value="Asia/Dubai">UAE (UTC+4)</option>
                <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                <option value="Europe/London">London (UTC+0/+1)</option>
                <option value="America/New_York">New York (UTC-5/-4)</option>
                <option value="America/Los_Angeles">Los Angeles (UTC-8/-7)</option>
                <option value="UTC">UTC</option>
              </select>
            </label>
          </div>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="chakra-submit">
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Mapping your energy body..." : "Reveal my chakra heat-map"}
          </button>
        </form>

        {/* Result */}
        {result && (
          <div className="mt-10 space-y-6" data-testid="chakra-result">
            {/* Hero summary */}
            <div className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40">
              <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
                <Sparkle size={14} weight="fill" /> Energetic Signature
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
                {result.dominant_chakra.name} <span className="italic text-amber-300">·</span> {result.dominant_purushartha.name}
              </h2>
              <p className="mt-3 text-amber-100/90 text-sm sm:text-base leading-relaxed max-w-2xl">{result.summary}</p>
              <div className="mt-5 grid grid-cols-3 gap-3">
                <Pill label="Lagna" value={result.lagna.rashi} />
                <Pill label="Moon Rashi" value={result.moon.rashi} />
                <Pill label="Sun Rashi" value={result.sun_rashi} />
              </div>
            </div>

            {/* Body heat-map — FREE */}
            <BodyHeatMap chakras={result.chakras} />

            {/* Premium: detailed chakra cards + purushartha donut */}
            <PaywallGate tool="chakras" toolLabel="Chakra & Purushartha reading" birthDetails={form} report={result}>
              <div className="space-y-6">
                <div className="card-surface rounded-2xl p-5 sm:p-7">
                  <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
                    <ChartBar size={14} weight="duotone" /> All 7 chakras · ranked
                  </div>
                  <div className="space-y-3">
                    {[...result.chakras].sort((a, b) => b.percent - a.percent).map((c) => (
                      <ChakraRow key={c.key} chakra={c} />
                    ))}
                  </div>
                </div>

                <PurusharthaPanel purusharthas={result.purusharthas} dominant={result.dominant_purushartha} />
              </div>
            </PaywallGate>

            {/* CTA — Mantra & Remedies specialist */}
            <ToolAstrologerCta
              requiredTechnique="Mantra Therapy & Planetary Remedies"
              pitch={`Want a live read on your dominant ${result.dominant_chakra.name} & ${result.dominant_purushartha.name} path — with personalised mantra remedies?`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// ---------- Sub-components ----------

function Pill({ label, value }) {
  return (
    <div className="rounded-lg bg-amber-900/40 px-3 py-2 border border-amber-700/30">
      <div className="text-amber-300/80 text-[10px] uppercase tracking-widest">{label}</div>
      <div className="mt-0.5 font-serif-display text-base sm:text-lg leading-tight text-amber-50">{value || "—"}</div>
    </div>
  );
}

function ChakraRow({ chakra }) {
  const Icon = CHAKRA_ICONS[chakra.key] || Sparkle;
  return (
    <details className="rounded-xl border border-soft bg-white/60 overflow-hidden group" data-testid={`chakra-row-${chakra.key}`}>
      <summary className="cursor-pointer list-none px-4 py-3 flex items-center gap-3 hover:bg-amber-50/40 transition-colors">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0"
          style={{ backgroundColor: chakra.color }}
        >
          <Icon size={18} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-serif-display text-base sm:text-lg text-[#3F2D1B]">{chakra.name}</span>
            <span className="text-xs text-muted">· {chakra.english}</span>
          </div>
          <div className="h-2 rounded-full bg-black/5 mt-1.5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${chakra.percent}%`, backgroundColor: chakra.color }}
            />
          </div>
        </div>
        <div className="text-right shrink-0 ml-2">
          <div className="font-serif-display text-lg sm:text-xl text-[#3F2D1B]">{chakra.percent}%</div>
          <div className="text-[10px] text-muted uppercase tracking-widest">active</div>
        </div>
      </summary>
      <div className="px-4 pb-4 pt-1 text-sm text-[#3F2D1B]/85 space-y-2.5">
        <p>{chakra.summary}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <Mini label="Body" value={chakra.body_part} />
          <Mini label="Element" value={chakra.element} />
          <Mini label="Bija mantra" value={chakra.bija} />
          <Mini label="Petals" value={chakra.petals} />
        </div>
        <div className="pt-2 border-t border-soft">
          <div className="label-eyebrow !text-[10px] text-emerald-700">When activated</div>
          <p className="text-xs mt-1 leading-snug">{chakra.when_high}</p>
        </div>
        <div>
          <div className="label-eyebrow !text-[10px] text-rose-700">When blocked</div>
          <p className="text-xs mt-1 leading-snug">{chakra.when_low}</p>
        </div>
        <div>
          <div className="label-eyebrow !text-[10px] text-gold">Balance practice</div>
          <p className="text-xs mt-1 leading-snug">{chakra.balance_practice}</p>
        </div>
      </div>
    </details>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow !text-[10px]">{label}</div>
      <div className="text-sm font-medium text-[#3F2D1B]">{value || "—"}</div>
    </div>
  );
}

function PurusharthaPanel({ purusharthas, dominant }) {
  const total = purusharthas.reduce((s, p) => s + p.percent, 0) || 100;
  // Donut chart angles
  let cumulative = 0;
  const segments = purusharthas.map((p) => {
    const startAngle = (cumulative / total) * 360;
    cumulative += p.percent;
    const endAngle = (cumulative / total) * 360;
    return { ...p, startAngle, endAngle };
  });

  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="purushartha-panel">
      <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
        <Sun size={14} weight="duotone" /> Four Purusharthas · your life-pursuit signature
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-center">
        <Donut segments={segments} dominant={dominant} />
        <div className="space-y-3">
          {purusharthas.map((p) => {
            const Icon = PURUSHARTHA_ICONS[p.key] || Sun;
            const isDominant = p.key === dominant.key;
            return (
              <details key={p.key} className={`rounded-xl border overflow-hidden ${isDominant ? "border-amber-400 bg-amber-50/60" : "border-soft bg-white/60"}`} data-testid={`purushartha-row-${p.key}`}>
                <summary className="cursor-pointer list-none px-4 py-3 flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0"
                    style={{ backgroundColor: p.color }}
                  >
                    <Icon size={18} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif-display text-base sm:text-lg text-[#3F2D1B]">{p.name}</span>
                      <span className="text-xs text-muted">· {p.meaning}</span>
                    </div>
                    <div className="h-2 rounded-full bg-black/5 mt-1.5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${p.percent}%`, backgroundColor: p.color }} />
                    </div>
                  </div>
                  <div className="font-serif-display text-lg sm:text-xl text-[#3F2D1B] ml-2">{p.percent}%</div>
                </summary>
                <div className="px-4 pb-4 pt-1 text-sm text-[#3F2D1B]/85 space-y-2">
                  <p>{p.summary}</p>
                  <div className="text-xs">
                    <div className="label-eyebrow !text-[10px] text-emerald-700">When activated</div>
                    <p className="mt-1">{p.when_high}</p>
                  </div>
                  <div className="text-xs">
                    <div className="label-eyebrow !text-[10px] text-rose-700">Shadow</div>
                    <p className="mt-1">{p.shadow}</p>
                  </div>
                  <div className="text-xs">
                    <div className="label-eyebrow !text-[10px] text-gold">Practice</div>
                    <p className="mt-1">{p.practice}</p>
                  </div>
                </div>
              </details>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Donut({ segments, dominant }) {
  const size = 200, stroke = 28, radius = (size - stroke) / 2, cx = size / 2, cy = size / 2;
  const polar = (angleDeg) => {
    const r = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(r), y: cy + radius * Math.sin(r) };
  };
  return (
    <div className="flex flex-col items-center">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-44 h-44 sm:w-52 sm:h-52" data-testid="purushartha-donut">
        {segments.map((seg) => {
          if (seg.percent <= 0) return null;
          const p1 = polar(seg.startAngle);
          const p2 = polar(seg.endAngle);
          const largeArc = seg.endAngle - seg.startAngle > 180 ? 1 : 0;
          const d = `M ${p1.x} ${p1.y} A ${radius} ${radius} 0 ${largeArc} 1 ${p2.x} ${p2.y}`;
          return (
            <path
              key={seg.key}
              d={d}
              stroke={seg.color}
              strokeWidth={stroke}
              fill="none"
              strokeLinecap="butt"
            />
          );
        })}
        <text x={cx} y={cy - 4} textAnchor="middle" className="font-serif-display" fontSize="22" fill="#3F2D1B">
          {`${dominant.percent}%`}
        </text>
        <text x={cx} y={cy + 18} textAnchor="middle" fontSize="11" fill="#3F2D1B" opacity="0.7">
          {dominant.name}
        </text>
      </svg>
    </div>
  );
}

function BodyHeatMap({ chakras }) {
  const W = 320, H = 900;
  const max = useMemo(() => Math.max(...chakras.map((c) => c.percent), 0.1), [chakras]);

  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="chakra-heatmap">
      <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-4">
        <Sparkle size={14} weight="duotone" /> Energy body heat-map
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div className="flex justify-center">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full max-w-[360px] h-auto"
            aria-label="Chakra heat-map on a human silhouette"
            role="img"
          >
            <defs>
              {chakras.map((c) => (
                <radialGradient id={`grad-${c.key}`} key={c.key}>
                  <stop offset="0%" stopColor={c.color} stopOpacity="0.95" />
                  <stop offset="60%" stopColor={c.color} stopOpacity="0.45" />
                  <stop offset="100%" stopColor={c.color} stopOpacity="0" />
                </radialGradient>
              ))}
            </defs>

            {/* Silhouette — stylised front-view (centered at x=110) */}
            <g transform="translate(10,0)" fill="#3F2D1B" opacity="0.10">
              <ellipse cx="100" cy="90" rx="42" ry="50" />
              <rect x="86" y="135" width="28" height="35" rx="4" />
              <path d="M 50 175 Q 100 158 150 175 L 158 380 Q 158 460 145 530 L 135 640 Q 130 700 125 760 L 75 760 Q 70 700 65 640 L 55 530 Q 42 460 42 380 Z" />
              <path d="M 50 180 Q 28 230 30 320 Q 32 400 28 460 L 18 540 Q 16 580 22 600 L 38 595 Q 44 540 48 460 Q 52 380 55 320 Q 58 230 60 195 Z" />
              <path d="M 150 180 Q 172 230 170 320 Q 168 400 172 460 L 182 540 Q 184 580 178 600 L 162 595 Q 156 540 152 460 Q 148 380 145 320 Q 142 230 140 195 Z" />
              <path d="M 78 760 Q 75 850 78 860 L 96 860 Q 99 800 98 760 Z" />
              <path d="M 102 760 Q 101 800 104 860 L 122 860 Q 125 850 122 760 Z" />
            </g>

            <g transform="translate(10,0)" fill="none" stroke="#3F2D1B" strokeWidth="1.4" opacity="0.45">
              <ellipse cx="100" cy="90" rx="42" ry="50" />
              <path d="M 86 135 L 86 170 Q 60 178 50 180 Q 28 230 30 320 Q 32 400 28 460 L 18 540 Q 16 580 22 600 L 38 595 Q 44 540 48 460 Q 52 380 55 320 Q 58 230 60 195" />
              <path d="M 114 135 L 114 170 Q 140 178 150 180 Q 172 230 170 320 Q 168 400 172 460 L 182 540 Q 184 580 178 600 L 162 595 Q 156 540 152 460 Q 148 380 145 320 Q 142 230 140 195" />
              <path d="M 42 380 Q 42 460 55 530 L 65 640 Q 70 700 75 760 L 78 860 L 96 860 Q 99 800 98 760 L 102 760 Q 101 800 104 860 L 122 860 Q 125 850 125 760 L 135 640 Q 130 700 145 530 Q 158 460 158 380" />
            </g>

            {/* Chakra glow circles — centered on body axis at x=110 */}
            {chakras.map((c) => {
              const cx = 110;
              const y = c.y_pct * H;
              const intensity = c.percent / max;
              const r = 32 + intensity * 44;
              return (
                <g key={c.key}>
                  <circle cx={cx} cy={y} r={r} fill={`url(#grad-${c.key})`} />
                  <circle cx={cx} cy={y} r={9 + intensity * 5} fill={c.color} opacity={0.9} />
                  {/* Connector line + right-side label */}
                  <line x1={cx + (9 + intensity * 5)} y1={y} x2={195} y2={y} stroke={c.color} strokeWidth="1" opacity="0.55" />
                  <text x={200} y={y - 2} fontSize="13" fill="#3F2D1B" fontWeight="700">{c.name}</text>
                  <text x={200} y={y + 14} fontSize="11" fill={c.color} fontWeight="700">{`${c.percent}%`}</text>
                  <text x={244} y={y + 14} fontSize="10" fill="#3F2D1B" opacity="0.55">{c.english}</text>
                </g>
              );
            })}
          </svg>
        </div>

        <div className="space-y-2 text-sm text-[#3F2D1B]/85">
          <p className="leading-relaxed">
            Each chakra's <b>glow size</b> and <b>intensity</b> reflects how much of your natal energy
            flows through it. Bigger and brighter = more active. Use this map to:
          </p>
          <ul className="text-xs space-y-1.5 list-disc list-inside text-[#3F2D1B]/80">
            <li>Identify your <b>signature chakra</b> — your default mode of being.</li>
            <li>Spot <b>dim</b> chakras — areas to consciously cultivate.</li>
            <li>Choose the right <b>asana / pranayama / mantra</b> for daily sadhana.</li>
            <li>Understand emotional patterns through your dominant energy centre.</li>
          </ul>
          <p className="text-xs text-muted pt-2 italic">
            Heat-map derives from your Lagna, Sun, Moon and all nine grahas — each rashi feeds its
            primary chakra at full weight and a secondary chakra at 35%.
          </p>
        </div>
      </div>
    </div>
  );
}
